import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LocalizedClerkProvider } from '@/components/LocalizedClerkProvider';
import { UserLanguageSync } from '@/features/auth/UserLanguageSync';
import { OrphanedSessionSignOut } from '@/features/auth/OrphanedSessionSignOut';
import { RoleProvider } from '@/contexts/RoleContext';
import { initSentry } from '@/lib/sentry';
import { initFreezeMonitor, sweepDeadSentinels } from '@/lib/freeze-monitor';
import { logWarning } from '@/lib/logger';
import { captureReferralCode } from '@/lib/referral';
import { captureOrgFromLink } from '@/lib/org-link';
import { retryStaleAccessOnce } from '@/lib/query-retry';
import { IS_EMBEDDED_SESSION } from '@/lib/embedded-session';

// Import the generated route tree
import { routeTree } from './routeTree.gen';

// Import i18n configuration (initializes i18next)
import '@/i18n/config';

// CSS
import './index.css';

// Query client with optimized caching
// NOTE: retry disabled by default to prevent cascade failures when backend is down
// Individual queries can enable retry if needed (e.g., retry: 1 for critical data)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - reduce unnecessary refetches
      gcTime: 10 * 60 * 1000, // 10 minutes garbage collection (formerly cacheTime)
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always',
      // Disabled to prevent cascade failures (was: 2) — except a late answer of
      // an older access revision, asked once more (E1.5 PR 4b).
      retry: retryStaleAccessOnce,
    },
  },
});

// The organization an email link is about (`?org=`, #1232) — read and removed
// from the address bar BEFORE the router is created, so the router never
// carries it into later navigations. OrganizationContext applies it.
captureOrgFromLink();

// Create router with queryClient in context — available in all route loaders.
// defaultPreload='intent' prefetches the target route's chunk and loader data
// on hover/focus, so by the time the user clicks the page is already warm.
// 50ms delay avoids prefetching every route the cursor passes over.
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 30_000,
  defaultPreloadDelay: 50,
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Clerk publishable key.
//
// Без фолбэка НАМЕРЕННО. Прежний `|| 'pk_test_YOUR_KEY_HERE'` превращал
// отсутствие ключа в белый экран у пользователя: сборка зелёная, деплой
// зелёный, а Clerk молча получал плейсхолдер. Ровно так стейджинг простоял
// 18.08, когда коммит 21edade90 случайно снёс `apps/ui-worker/.env.staging`
// (#352). Теперь отсутствие ключа ловит сборка (`requireAuthEnv` в
// vite.config.ts), и до браузера этот случай не доходит вовсе.
const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

// Detect embedded mode BEFORE React renders to avoid initializing Clerk in Bitrix24 iframe.
// Clerk SDK shares cookies/localStorage on the same domain, which conflicts with
// the desktop browser session when both run on the same origin.
const isEmbeddedRoute = IS_EMBEDDED_SESSION;

// Initialize Sentry if DSN is provided
initSentry()
  .catch((err) => {
    logWarning('Failed to initialize Sentry', {
      module: 'Main',
      error: err instanceof Error ? err.message : String(err),
    });
  })
  .finally(() => {
    // Sweep dead sentinels only AFTER Sentry init settles: the sweep both
    // reports via captureMessage (console fallback if Sentry never came up)
    // and deletes the evidence — sweeping pre-init would silently drop every
    // terminal-freeze report. Also keeps the localStorage scan off the
    // synchronous boot path.
    try {
      sweepDeadSentinels();
    } catch {
      // Never break boot because monitoring failed.
    }
  });

// Watch for main-thread freezes and report to Sentry with route + recordId.
// Independent of Sentry init: breadcrumbs are no-ops if Sentry isn't ready,
// console.warn still fires in dev.
initFreezeMonitor();

// Capture a partner referral code from `?ref=` before Clerk's sign-up flow can
// rewrite the URL. Persisted to localStorage so it survives the redirect dance
// and is attached to the onboarding request (see lib/referral.ts). No-op when
// the param is absent.
captureReferralCode();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {isEmbeddedRoute ? (
      <QueryClientProvider client={queryClient}>
        <React.Suspense fallback={null}>
          <RouterProvider router={router} />
        </React.Suspense>
      </QueryClientProvider>
    ) : (
      <LocalizedClerkProvider
        publishableKey={CLERK_PUBLISHABLE_KEY}
        signInUrl="/sign-in"
        signUpUrl="/sign-up"
        signInFallbackRedirectUrl={`${window.location.origin}/dashboard`}
        signUpFallbackRedirectUrl={`${window.location.origin}/onboarding`}
        signInForceRedirectUrl={`${window.location.origin}/dashboard`}
        signUpForceRedirectUrl={`${window.location.origin}/onboarding`}
        afterSignOutUrl={`${window.location.origin}/sign-in`}
      >
        <QueryClientProvider client={queryClient}>
          {/* Isolated leaf: syncs language ⇄ Clerk profile without re-rendering
              the router tree (see UserLanguageSync). */}
          <UserLanguageSync />
          {/* Signs out sessions whose Aelo user row was deleted server-side. */}
          <OrphanedSessionSignOut />
          <React.Suspense fallback={null}>
            <RoleProvider>
              <RouterProvider router={router} />
            </RoleProvider>
          </React.Suspense>
        </QueryClientProvider>
      </LocalizedClerkProvider>
    )}
  </React.StrictMode>
);
